import { useCallback, useRef } from 'react';
import type { Node, Edge } from '@xyflow/react';
import {
  ProcessFlowContextMenu,
  classifyCanvasNode,
  isRuleHandleId,
  type CanvasNodeKind,
  type ContextMenuTarget,
  type EdgeContextAction,
  type EdgeDescriptor,
  type EndNodeData,
  type NodeClassifierContext
} from '@bliks-io/ui';
import { getEdgeActionCapabilities } from '../edge-actions';
import type { useUndoableEditorActions } from './useUndoableEditorActions';
import type { useSwimlaneActions } from './useSwimlaneActions';
import type { useNodeContextMenuHandlers } from './useNodeContextMenuHandlers';

type UndoableActions = ReturnType<typeof useUndoableEditorActions>;
type SwimlaneActions = ReturnType<typeof useSwimlaneActions>;
type NodeContextMenuHandlers = ReturnType<typeof useNodeContextMenuHandlers>;

// Exported for the context-menu regression tests (bug 4: the start event
// must yield a node target so Edit/Delete render).
export function nodeToContextMenuTarget(node: Node): ContextMenuTarget | null {
  const nodeKind = classifyCanvasNode(node);
  switch (nodeKind) {
    case 'swimlane': {
      const laneKey = (node.data as { laneKey?: string } | undefined)?.laneKey;
      return laneKey ? { kind: 'swimlane', laneKey } : null;
    }
    case 'step':
    case 'decision':
    case 'assignment':
    case 'event-start':
    case 'event-intermediate':
    case 'event-legacy':
      return { kind: 'node', nodeId: node.id, nodeKind };
    case 'end': {
      // Only amber UNCLEAR ends get a menu — plain NONE ends have no user-
      // meaningful actions behind them.
      const data = node.data as EndNodeData | undefined;
      return data?.variant === 'unclear' ? { kind: 'node', nodeId: node.id, nodeKind } : null;
    }
    // Deliberately menu-less: the `+` gateway is a derived rendering artifact
    // (today's menu there only offered a dead Edit entry).
    case 'synthetic-gateway':
      return null;
    // Notes have their own context menu, not the step/event node menu.
    case 'note':
      return null;
    // The empty-step placeholder suppresses the menu itself; it has no actions.
    case 'empty-step':
      return null;
    default: {
      nodeKind satisfies never;
      return null;
    }
  }
}

// Absolute flow coords → lane-relative coords for the lane the new node will
// land in (the clicked lane, or the implicit `unassigned` lane). The hint is
// only forwarded for real lanes — `unassigned` resolves the same way without
// one, so sending it would just clutter the placeholder.
export function toClickedLaneAnchor(
  position: { x: number; y: number },
  laneKey: string | undefined,
  toLaneRelative: (p: { x: number; y: number }, k: string) => { x: number; y: number }
): { position: { x: number; y: number }; laneKey?: string } {
  return {
    position: toLaneRelative(position, laneKey ?? 'unassigned'),
    laneKey: laneKey && laneKey !== 'unassigned' ? laneKey : undefined
  };
}

export function useDraftEditorContextMenu(params: {
  undoableActions: UndoableActions;
  swimlaneActions: SwimlaneActions;
  nodeHandlers: NodeContextMenuHandlers;
  handleDeleteNode: (nodeId: string, nodeKind: CanvasNodeKind) => void;
  /** Raw-id event-node classification (ADR 0003) for edge menus. */
  classifyContext?: NodeClassifierContext;
  /** Omitted when the user may not create step templates — hides the menu entry. */
  onSaveAsStepTemplate?: (nodeIds: string[]) => void;
  /** Omitted when the user may not read step templates — hides the menu entry. */
  onInsertStepTemplate?: (position: { x: number; y: number }, laneKey?: string) => void;
}) {
  const {
    undoableActions,
    swimlaneActions,
    nodeHandlers,
    handleDeleteNode,
    classifyContext,
    onSaveAsStepTemplate,
    onInsertStepTemplate
  } = params;
  const ctxTargetRef = useRef<ContextMenuTarget | null>(null);

  const handlePaneContextMenu = useCallback(
    (_event: React.MouseEvent, position: { x: number; y: number }) => {
      // Right-clicks on the empty body of a swimlane fall through to the
      // pane (the SwimlaneNode SVG is `pointer-events: none` so panning still
      // works). Tag the pane target with the lane key so the menu surfaces
      // both add-step (pane) and move-up/down (swimlane) actions.
      const laneKey = swimlaneActions.findSwimlaneAt(position);
      ctxTargetRef.current = { kind: 'pane', position, laneKey: laneKey ?? undefined };
    },
    [swimlaneActions]
  );

  const handleNodeContextMenu = useCallback((event: React.MouseEvent, node: Node) => {
    // Decision gateways render their output handles as `<Handle id="rule-…">`.
    // xyflow exposes the id via `data-handleid` on the rendered DOM element.
    // If the right-click hit one of those, prefer the per-handle target so
    // the menu offers "Mark as End/Unclear" scoped to that output instead of
    // the node-level options.
    const domTarget = event.target as HTMLElement | null;
    const handleEl = domTarget?.closest?.('[data-handleid]') as HTMLElement | null;
    const handleId = handleEl?.dataset.handleid;
    if (handleId && isRuleHandleId(handleId)) {
      ctxTargetRef.current = { kind: 'handle', nodeId: node.id, handleId };
      return;
    }
    const target = nodeToContextMenuTarget(node);
    if (target) ctxTargetRef.current = target;
  }, []);

  const handleEdgeContextMenu = useCallback((_event: React.MouseEvent, edge: Edge) => {
    ctxTargetRef.current = {
      kind: 'edge',
      source: edge.source,
      target: edge.target,
      sourceHandle: edge.sourceHandle ?? null
    };
  }, []);

  const handleAddStepAtPosition = useCallback(
    (position: { x: number; y: number }, laneKey?: string) => {
      const anchor = toClickedLaneAnchor(position, laneKey, swimlaneActions.toLaneRelativePosition);
      void undoableActions.addStep(anchor.position, anchor.laneKey);
    },
    [undoableActions, swimlaneActions]
  );

  const handleInsertStepTemplateAtPosition = useCallback(
    (position: { x: number; y: number }, laneKey?: string) => {
      const anchor = toClickedLaneAnchor(position, laneKey, swimlaneActions.toLaneRelativePosition);
      onInsertStepTemplate?.(anchor.position, anchor.laneKey);
    },
    [swimlaneActions, onInsertStepTemplate]
  );

  const handleEdgeAction = useCallback(
    (action: EdgeContextAction, edge: EdgeDescriptor) => {
      void undoableActions.onEdgeAction(action, edge);
    },
    [undoableActions]
  );

  const contextMenuWrapper = useCallback(
    (children: React.ReactNode) => (
      <ProcessFlowContextMenu
        targetRef={ctxTargetRef}
        classifyContext={classifyContext}
        onAddStep={handleAddStepAtPosition}
        onAddEvent={(pos, attachToStepId) => void undoableActions.addEvent(pos, attachToStepId)}
        canAddEventForStep={nodeHandlers.canAddEventForNode}
        onEditNode={nodeHandlers.handleEditNode}
        onSaveAsStepTemplate={onSaveAsStepTemplate}
        onInsertStepTemplate={onInsertStepTemplate ? handleInsertStepTemplateAtPosition : undefined}
        onDeleteNode={handleDeleteNode}
        canDeleteNode={nodeHandlers.canDeleteNode}
        onMarkAsEnd={nodeHandlers.handleMarkAsEnd}
        canMarkAsEnd={nodeHandlers.canMarkAsEndForNode}
        onConvertToAssignment={nodeHandlers.handleConvertToAssignment}
        canConvertToAssignment={nodeHandlers.canConvertToAssignmentForNode}
        onConvertToStep={nodeHandlers.handleConvertToStep}
        canConvertToStep={nodeHandlers.canConvertToStepForNode}
        onMergeNode={nodeHandlers.handleMergeNode}
        canMergeNode={nodeHandlers.canMergeNodeForNode}
        mergeNodeDisabledReason={nodeHandlers.mergeNodeDisabledReasonForNode}
        onEdgeAction={handleEdgeAction}
        getEdgeActionCapabilities={getEdgeActionCapabilities}
        onMoveSwimlane={swimlaneActions.onContextMenuMove}
        getSwimlaneActionCapabilities={swimlaneActions.getCapabilities}
        onSetSwimlaneColor={swimlaneActions.onContextMenuSetColor}
        getSwimlaneColor={swimlaneActions.getColor}
        onSetHandleTerminal={nodeHandlers.handleSetHandleTerminal}
        canSetHandleTerminal={nodeHandlers.canSetHandleTerminal}
      >
        {children}
      </ProcessFlowContextMenu>
    ),
    [
      handleAddStepAtPosition,
      undoableActions,
      nodeHandlers,
      handleDeleteNode,
      handleEdgeAction,
      swimlaneActions,
      classifyContext,
      onSaveAsStepTemplate,
      onInsertStepTemplate,
      handleInsertStepTemplateAtPosition
    ]
  );

  return {
    handlePaneContextMenu,
    handleNodeContextMenu,
    handleEdgeContextMenu,
    contextMenuWrapper
  };
}
